import { Pause, Play } from 'lucide-react';
import { allTracks, useBgmStore } from '@/store/bgmStore';
import { Button } from '../Button';
import { Range } from '../Range';

export const BgmSettings = function BgmSettings() {
  const {
    setVolume,
    isBgmEnabled,
    toggleBgm,
    volume,
    playNext,
    currentBgmTitle,
    checkedTitles,
    toggleTrack,
    setCheckedTitles,
    applyChecklist,
    pause,
    resume,
    isCurrentlyPlaying,
  } = useBgmStore();

  const handleBgmVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value, 10);
    setVolume(newVolume);
  };

  const handleToggleBgm = () => {
    toggleBgm();
  };

  const handleToggleTrack = (title: string) => {
    toggleTrack(title);
    applyChecklist();
  };

  const handleSelectAll = () => {
    setCheckedTitles(allTracks.map((t) => t.title));
    applyChecklist();
  };

  const handleDeselectAll = () => {
    setCheckedTitles([]);
    applyChecklist();
  };

  return (
    <section className="grid gap-16 grid-cols-1">
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-800">BGM</h3>
          <p className="text-gray-600">ゲーム中のBGMを再生</p>
        </div>
        <button
          onClick={handleToggleBgm}
          aria-label={isBgmEnabled ? 'BGMをオフにする' : 'BGMをオンにする'}
          aria-checked={isBgmEnabled}
          role="switch"
          className={`relative h-[1em] w-[1.8em] flex items-center justify-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:ring-offset-2 text-3xl ${
            isBgmEnabled ? 'bg-yellow-600' : 'bg-gray-300'
          }`}
          type="button"
        >
          <span
            className={`absolute h-[0.8em] w-[0.8em] transform rounded-full bg-white transition-transform duration-200 ${
              isBgmEnabled ? 'translate-x-[50%]' : 'translate-x-[-50%]'
            }`}
            aria-hidden="true"
          />
        </button>
      </header>

      {/* BGM音量調整 */}
      <section className=" border-gray-200">
        <header className="flex items-center justify-between text-md">
          <h4 className="font-bold text-gray-800">BGM音量</h4>
          <b className="text-[1.2em] text-yellow-600">{volume}</b>
        </header>

        <div>
          <Range
            value={volume}
            onChange={handleBgmVolumeChange}
            ariaLabel="BGM音量"
            disabled={!isBgmEnabled}
            style={{
              background: isBgmEnabled
                ? `linear-gradient(to right, var(--color-orange-800) 0%, var(--color-yellow-500) ${volume}%, var(--color-white) ${volume}%, var(--color-white) 100%)`
                : undefined,
            }}
          />
        </div>
      </section>

      <div className="flex items-center justify-between gap-8">
        {isCurrentlyPlaying ? (
          <Button onClick={() => pause()} className="p-10" invert>
            <Pause className="size-20" fill="currentColor" strokeWidth={0} />
          </Button>
        ) : (
          <Button onClick={() => resume()} className="p-10" invert>
            <Play className="size-20" fill="currentColor" strokeWidth={0} />
          </Button>
        )}
        <Button
          onClick={playNext}
          disabled={!isBgmEnabled}
          className="shrink-0 text-base"
          invert
        >
          次の曲を再生
        </Button>
        <div className="text-right grid">
          <p className="text-xs text-yellow-700">♪Now Playing</p>
          <p className="max-w-full w-full  text-base font-bold text-gray-800 overflow-hidden whitespace-nowrap">
            {currentBgmTitle ? (
              <span
                className="flex w-max"
                style={{ animation: 'carousel 10s linear infinite' }}
              >
                <span className="pr-[0.5em]">{currentBgmTitle}</span>
                <span className="pr-[0.5em]">～</span>
                <span className="pr-[0.5em]">{currentBgmTitle}</span>
                <span className="pr-[0.5em]">～</span>
                <span className="pr-[0.5em]">{currentBgmTitle}</span>
                <span className="pr-[0.5em]">～</span>
                <span className="pr-[0.5em]">{currentBgmTitle}</span>
                <span className="pr-[0.5em]">～</span>
                <span className="pr-[0.5em]">{currentBgmTitle}</span>
                <span className="pr-[0.5em]">～</span>
                <span className="pr-[0.5em]">{currentBgmTitle}</span>
                <span className="pr-[0.5em]">～</span>
              </span>
            ) : (
              <span>...</span>
            )}
          </p>
        </div>
      </div>

      {/* 再生リスト */}
      <section className="border-t border-gray-200 pt-16 grid gap-8">
        <header className="flex items-center justify-between">
          <h4 className="font-bold text-gray-800 text-md">再生リスト</h4>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-yellow-700 underline hover:text-yellow-900"
            >
              全選択
            </button>
            <span className="text-xs text-gray-400">/</span>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-xs text-yellow-700 underline hover:text-yellow-900"
            >
              全解除
            </button>
          </div>
        </header>

        <ul className="max-h-200 overflow-y-auto border border-gray-200 rounded-md">
          {allTracks.map((track) => (
            <li
              key={track.title}
              className="border-b border-gray-100 last:border-b-0"
            >
              <label className="flex items-center gap-8 px-12 py-8 cursor-pointer hover:bg-yellow-50 transition-colors">
                <input
                  type="checkbox"
                  checked={checkedTitles.includes(track.title)}
                  onChange={() => handleToggleTrack(track.title)}
                  className="size-16 accent-yellow-600 shrink-0"
                />
                <span className="text-sm text-gray-800 truncate">
                  {track.title}
                </span>
              </label>
            </li>
          ))}
        </ul>

        <p className="text-xs text-gray-500 mt-8">
          ※未選択の場合は全曲シャッフル再生
        </p>
      </section>
    </section>
  );
};
